/**
 * Formatting + localisation helpers for label printing.
 *
 * IDR uses Indonesian thousand separators (dots) and the "Rp" prefix —
 * e.g. `2500000` → `"Rp 2.500.000"`. `Intl.NumberFormat` in `id-ID` locale
 * gives us this natively.
 */

/** Format an IDR amount with Indonesian thousand-separators. */
export function formatIDR(amount) {
  if (amount === null || amount === undefined || amount === "") return "Rp 0";
  const n = Number(amount);
  if (Number.isNaN(n)) return "Rp 0";
  // `id-ID` locale gives "2.500.000", we prepend "Rp " ourselves so the
  // spacing matches the app's existing convention across invoices/PDFs.
  return `Rp ${new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 }).format(Math.round(n))}`;
}

/** Return the display value for a product field, respecting hide-if-empty. */
export function pickField(product, field) {
  const raw = product?.[field];
  if (raw === null || raw === undefined) return "";
  return String(raw).trim();
}

/**
 * Bilingual field labels (English + Indonesian).
 * The label modal exposes a language toggle; the printed layout uses the
 * matching set. Everything else on the label is data (SKU, price…) which
 * doesn't need translation.
 */
export const LABEL_STRINGS = {
  en: {
    header: "OPIK JEWELS",
    name: "Name",
    dimension: "Dimension",
    sku: "SKU",
    ring_size: "Ring Size",
    metal: "Metal",
    price: "Price",
    scan_to_view: "Scan to view",
  },
  id: {
    header: "OPIK JEWELS",
    name: "Nama",
    dimension: "Dimensi",
    sku: "SKU",
    ring_size: "Ukuran Cincin",
    metal: "Logam",
    price: "Harga",
    scan_to_view: "Pindai untuk melihat",
  },
};

/**
 * Compile the visible rows of a label for the given product + template.
 * Skips rows where the source field is empty (spec: "if Ring Size or Metal
 * is empty, hide that row automatically") so we never print a dangling
 * "Ring Size: —" line on a loose stone.
 */
export function buildLabelRows(product, template, lang = "en") {
  const t = LABEL_STRINGS[lang] || LABEL_STRINGS.en;
  const rows = [];
  const push = (visibleFlag, label, value) => {
    if (!visibleFlag) return;
    const v = String(value ?? "").trim();
    if (!v) return; // hide-if-empty
    rows.push({ label, value: v });
  };
  push(template.show_name, t.name, product.name);
  push(template.show_dimension, t.dimension, product.dimension || product.size_mm);
  push(template.show_sku, t.sku, product.sku);
  push(template.show_ring_size, t.ring_size, product.ring_size);
  push(template.show_metal, t.metal, product.metal || product.metal_custom);
  push(template.show_price, t.price, formatIDR(product.selling_price));
  return rows;
}

/**
 * Build the public URL a QR code should encode. Prefers the settings-based
 * `public_qr_domain` if the caller supplies one; falls back to the current
 * origin. `qr_short_id` (already-minted) trumps SKU so scans go to the
 * pretty public page instead of the raw SKU string.
 */
export function productQRUrl(product, publicBase) {
  const base = (publicBase || window.location.origin).replace(/\/$/, "");
  const slug = product.qr_short_id || product.sku;
  return `${base}/p/${slug}`;
}
